import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — please sign in");
      navigate("/staff/login");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F2547] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
        <h1 className="font-heading text-2xl font-bold text-[#0F2547] mb-2">Choose a new password</h1>
        {!token ? (
          <div data-testid="reset-password-invalid">
            <p className="text-sm text-slate-500 my-4">This reset link is missing its token. Please request a new one.</p>
            <Link to="/forgot-password" className="text-sm font-semibold text-[#005EB8] hover:underline">Request a reset link</Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-password-form">
            <p className="text-sm text-slate-500 mb-6">Your new password must be at least 8 characters.</p>
            {error && <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-[#D5281B] text-sm px-4 py-3" data-testid="reset-password-error">{error}</div>}
            <div className="relative mb-4">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="reset-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-12 rounded-lg border border-slate-300 pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]"
                placeholder="New password"
              />
            </div>
            <div className="relative mb-6">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="reset-password-confirm-input"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full h-12 rounded-lg border border-slate-300 pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]"
                placeholder="Confirm new password"
              />
            </div>
            <button
              data-testid="reset-password-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white font-semibold text-sm disabled:opacity-60 mb-4"
            >
              {loading ? "Updating…" : "Update password"}
            </button>
            <Link to="/staff/login" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-[#005EB8]" data-testid="back-to-signin-link">
              <ChevronLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
